import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Archive, Download, Plus, Snowflake, Undo2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  POSITION_STATUSES,
  POSITION_STATUS_LABELS,
  formatMoney,
  isCurrencyCode,
  type CurrencyCode,
  type PositionStatus,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { DataTable } from '../../../components/data-table/DataTable';
import { TableToolbar } from '../../../components/data-table/TableToolbar';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { MultiSelect, Select } from '../../../components/Select';
import {
  EmptyState,
  FilteredEmptyState,
  ForbiddenState,
} from '../../../components/system-states/SystemState';
import { EMPTY, formatDate, formatNumber } from '../../../lib/format';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { scopeQuery, useCanEdit, useCanView, useSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { NewPositionDialog } from '../components/NewPositionDialog';
import { ARCHIVED_TONE, POSITION_STATUS_TONES } from '../../../components/status-tones';
import { PositionStatusChip } from '../components/PositionStatusChip';
import { SalaryBand } from '../components/SalaryBand';
import {
  positionExportUrl,
  useArchivePosition,
  usePositions,
  useRestorePosition,
  type PositionRow,
} from '../api/organisation-api';
import styles from './PositionLedgerPage.module.css';
import fit from '../../../components/FitPage.module.css';

/**
 * ORG-02 — the position ledger.
 *
 * E1's column list, and one thing it implies that is easy to miss: **vacant-since
 * is not a column in the database.** It is computed from the event log on the way
 * out (non-negotiable #10), which is why it is right even for a seat that has been
 * filled and emptied three times.
 *
 * The org-tree scope narrows the list without being a filter chip the user has to
 * find and remove — it is shown as one, because a list silently narrowed by a
 * setting made on another screen is the highest-cost navigation bug there is
 * (06-qol §8).
 */

const PAGE_SIZE = 50;

type ArchivedView = '1' | 'only';

const ARCHIVED_OPTIONS = [
  { value: 'hide', label: 'Hide archived seats' },
  { value: '1', label: 'Include archived seats' },
  { value: 'only', label: 'Archived seats only' },
];

export interface PositionLedgerSearch {
  readonly q?: string;
  /** One or more statuses, comma-separated, so "planned and vacant" is one link. */
  readonly status?: string;
  /** `1` shows archived seats alongside live ones; `only` shows nothing else. */
  readonly archived?: ArchivedView;
  /** Set by the command palette's "Add a position", so the dialog opens on arrival. */
  readonly create?: string;
}

export function PositionLedgerPage(): ReactNode {
  const canView = useCanView('org_unit_position');
  const canEdit = useCanEdit('org_unit_position');
  const navigate = useNavigate();
  const search = useSearch({ from: '/app-layout/organisation/positions' }) as PositionLedgerSearch;
  const scope = useSession((state) => state.scope);
  const setScope = useSession((state) => state.setScope);
  const showToast = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);

  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  // The palette can ask for the dialog (`?create=1`), which is why this is derived
  // from the URL rather than owned outright — a deep link has to be able to open it.
  const [isCreatingLocally, setIsCreatingLocally] = useState(false);
  const isCreating = isCreatingLocally || search.create === '1';
  function closeCreate(): void {
    setIsCreatingLocally(false);
    if (search.create !== undefined) setSearch({ ...search, create: undefined });
  }

  const archive = useArchivePosition();
  const restore = useRestorePosition();

  // 06-qol §3 and §6: every filter lives in the URL, so the list is shareable by
  // copying the address bar and a colleague opening it sees the identical view.
  const query = search.q ?? '';
  const statuses = (search.status?.split(',') ?? []).filter(isPositionStatus);
  const archivedView = search.archived ?? null;

  function setSearch(next: PositionLedgerSearch): void {
    void navigate({ to: '/organisation/positions', search: next, replace: true });
  }

  const params = {
    page: 1,
    pageSize: PAGE_SIZE,
    ...(query.trim().length >= 2 ? { query: query.trim() } : {}),
    ...(statuses.length > 0 ? { status: statuses.join(',') } : {}),
    ...(archivedView === '1' ? { includeArchived: true } : {}),
    ...(archivedView === 'only' ? { archivedOnly: true } : {}),
    ...scopeQuery(scope),
  };

  const positions = usePositions(params);

  if (!canView) {
    return <ForbiddenState resource="the position ledger" contact="Your admin" />;
  }

  const rows = positions.data?.rows ?? [];
  const total = positions.data?.total ?? 0;

  const filters = [
    ...(statuses.length > 0
      ? [
          {
            id: 'status',
            label: `Status: ${statuses.map((value) => POSITION_STATUS_LABELS[value]).join(', ')}`,
            onRemove: () => setSearch({ ...search, status: undefined }),
          },
        ]
      : []),
    ...(scope.kind !== 'all'
      ? [
          {
            id: 'scope',
            // Named as a chip so the narrowing is visible on the screen it affects,
            // not only on the tree where it was chosen.
            label: `Unit: ${scope.label ?? 'selected'}`,
            onRemove: () => setScope({ kind: 'all' }),
          },
        ]
      : []),
    ...(archivedView !== null
      ? [
          {
            id: 'archived',
            label: archivedView === 'only' ? 'Archived only' : 'Including archived',
            onRemove: () => setSearch({ ...search, archived: undefined }),
          },
        ]
      : []),
  ];

  const columns: readonly Column<PositionRow>[] = [
    {
      id: 'code',
      header: 'Code',
      isIdentity: true,
      defaultWidth: 140,
      cell: (row) => (
        <Link
          to="/organisation/positions/$positionId"
          params={{ positionId: row.id }}
          className={styles.code}
        >
          {row.code}
        </Link>
      ),
      exportValue: (row) => row.code,
    },
    { id: 'title', header: 'Title', defaultWidth: 220, cell: (row) => row.title },
    {
      id: 'unit',
      header: 'Unit',
      defaultWidth: 220,
      cell: (row) => (
        <span className={styles.unit}>
          <span>{row.departmentName}</span>
          <span className={styles.unitMeta}>
            {row.ventureName}
            {row.teamName !== null ? ` · ${row.teamName}` : ''}
          </span>
        </span>
      ),
      exportValue: (row) => [row.departmentName, row.ventureName, row.teamName].filter((part) => part !== null).join(' · '),
    },
    {
      id: 'salaryBand',
      header: 'Salary band',
      defaultWidth: 190,
      // Renders an em dash when §L23 hides it. The masking happens on the server,
      // in the serialisation layer — this component only reads what arrived.
      cell: (row) => <SalaryBand position={row} />,
      exportValue: salaryBandText,
    },
    {
      id: 'status',
      header: 'Status',
      defaultWidth: 150,
      cell: (row) => <PositionStatusChip position={row} />,
      exportValue: (row) => (row.archivedAt === null ? POSITION_STATUS_LABELS[row.status] : 'Archived'),
    },
    {
      id: 'hiringManagerName',
      header: 'Hiring manager',
      defaultWidth: 170,
      cell: (row) => row.hiringManagerName,
    },
    {
      id: 'vacantSince',
      header: 'Vacant since',
      defaultWidth: 140,
      cell: (row) => (row.vacantSince === null ? null : formatDate(row.vacantSince)),
    },
    {
      id: 'daysVacant',
      header: 'Days vacant',
      isNumeric: true,
      defaultWidth: 120,
      cell: (row) => (row.daysVacant === null ? null : formatNumber(row.daysVacant)),
    },
    {
      id: 'slots',
      header: 'Seats',
      isNumeric: true,
      defaultWidth: 90,
      cell: (row) => `${row.openSlots}/${row.totalSlots}`,
    },
  ];

  async function handleArchive(row: PositionRow): Promise<void> {
    try {
      const archived = await archive.mutateAsync({ id: row.id, version: row.version });
      // §5a rule 8: undo beats confirm. Archiving is reversible, so it just happens
      // and the ten-second window is the safety net.
      showUndo(`${row.code} archived`, () => {
        void restore.mutateAsync({ id: archived.id, version: archived.version });
      });
    } catch (cause) {
      showToast({
        tone: 'danger',
        message: `${row.code} was not archived`,
        detail: cause instanceof Error ? cause.message : undefined,
      });
    }
  }

  return (
    <div className={`${styles.page} ${fit.fit}`}>
      <TableToolbar
        title="Positions"
        total={total}
        query={query}
        onQueryChange={(next) => setSearch({ ...search, q: next === '' ? undefined : next })}
        filters={filters}
        onClearFilters={() => {
          setSearch({});
          setScope({ kind: 'all' });
        }}
        filterMenu={
          <div className={styles.filterMenu}>
            <MultiSelect
              label="Status"
              placeholder="Any status"
              value={statuses}
              options={POSITION_STATUSES.map((value) => ({
                value,
                label: POSITION_STATUS_LABELS[value],
              }))}
              onChange={(next) =>
                setSearch({ ...search, status: next.length > 0 ? next.join(',') : undefined })
              }
            />
            <Select
              label="Archived seats"
              value={archivedView ?? 'hide'}
              options={ARCHIVED_OPTIONS}
              onChange={(value) =>
                setSearch({
                  ...search,
                  archived: value === '1' || value === 'only' ? value : undefined,
                })
              }
            />
          </div>
        }
      />

      <div className={styles.actions}>
        <Button variant="ghost" icon={Download} onClick={() => window.open(positionExportUrl(params), '_self')}>
          Export
        </Button>
        {canEdit && (
          <Button variant="solid" icon={Plus} onClick={() => setIsCreatingLocally(true)}>
            Add a position
          </Button>
        )}
      </div>

      <DataTable
        screenId="position-ledger"
        label="Positions"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        toneOf={(row) => (row.archivedAt !== null ? ARCHIVED_TONE : POSITION_STATUS_TONES[row.status])}
        total={total}
        sort={sort}
        onSortChange={setSort}
        selection={selection}
        onSelectionChange={setSelection}
        onOpenRow={(row) =>
          void navigate({
            to: '/organisation/positions/$positionId',
            params: { positionId: row.id },
          })
        }
        linkOf={(row) => `/organisation/positions/${row.id}`}
        exportName="positions"
        views={{
          screen: 'positions',
          filters: viewFilters(search),
          onApply: (next) =>
            setSearch({
              q: next.q,
              status: next.status,
              archived: next.archived === '1' || next.archived === 'only' ? next.archived : undefined,
            }),
        }}
        rowActions={
          canEdit
            ? (row) =>
                row.archivedAt !== null ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={Undo2}
                    onClick={() => void restore.mutateAsync({ id: row.id, version: row.version })}
                  >
                    Restore
                  </Button>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={Snowflake}
                      onClick={() =>
                        void navigate({
                          to: '/organisation/positions/$positionId',
                          params: { positionId: row.id },
                        })
                      }
                    >
                      Status
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={Archive}
                      onClick={() => void handleArchive(row)}
                    >
                      Archive
                    </Button>
                  </>
                )
            : undefined
        }
        isLoading={positions.isPending}
        emptyState={
          filters.length > 0 || query !== '' ? (
            <FilteredEmptyState
              filterCount={filters.length + (query === '' ? 0 : 1)}
              onClearFilters={() => {
                setSearch({});
                setScope({ kind: 'all' });
              }}
            />
          ) : (
            <EmptyState
              title="No positions yet"
              body="A position is one budgeted seat. Add one and it appears here, in the org tree, and in every headcount report."
              action={
                canEdit ? (
                  <Button variant="solid" icon={Plus} onClick={() => setIsCreatingLocally(true)}>
                    Add a position
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />

      {isCreating && <NewPositionDialog onClose={closeCreate} />}
    </div>
  );
}

/**
 * S32 (Q28): a saved view keeps the address bar's filters. `create` is an instruction to open a
 * dialog, not a filter, so it is never saved.
 */
function viewFilters(search: PositionLedgerSearch): Record<string, string> {
  const entries: [string, unknown][] = Object.entries(search);
  const filters: Record<string, string> = {};
  for (const [key, value] of entries) {
    if (key !== 'create' && typeof value === 'string') filters[key] = value;
  }
  return filters;
}

/** The band as `SalaryBand` draws it, so an export never says more than the screen (#5). */
function salaryBandText(row: PositionRow): string {
  if (row.restrictedFields?.includes('salaryBandMinMinor') === true) return EMPTY;
  const { salaryBandMinMinor: min, salaryBandMaxMinor: max, salaryCurrency } = row;
  if (salaryCurrency === null || !isCurrencyCode(salaryCurrency) || (min === null && max === null)) return EMPTY;
  return `${moneyText(min, salaryCurrency)} – ${moneyText(max, salaryCurrency)}`;
}

function moneyText(amountMinor: string | null, currency: CurrencyCode): string {
  return amountMinor === null ? EMPTY : formatMoney({ amountMinor: BigInt(amountMinor), currency });
}

function isPositionStatus(value: string): value is PositionStatus {
  return POSITION_STATUSES.some((status) => status === value);
}
